import React, { createContext, useContext, type ReactNode } from 'react'
import { useWorkspace } from '../hooks/useWorkspace'
import type { Workspace } from '../services/apiService'

interface WorkspaceContextType {
  workspaces: Workspace[]
  activeWorkspace: Workspace | null
  loading: boolean
  error: string | null
  hasWorkspaces: boolean
  setActiveWorkspace: (workspace: Workspace) => void
  loadWorkspaces: () => Promise<void>
  createWorkspace: (data: {
    name: string
    description?: string
    clientOrganization?: string
    organizationId?: string
    templateId?: string
    settings?: Record<string, any>
  }) => Promise<Workspace>
  archiveWorkspace: (workspaceId: string) => Promise<void>
  permanentlyDeleteWorkspace: (workspaceId: string) => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

interface WorkspaceProviderProps {
  children: ReactNode
  enabled?: boolean
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({
  children,
  enabled = true,
}) => {
  const workspaceState = useWorkspace({ enabled })

  return (
    <WorkspaceContext.Provider value={workspaceState}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export const useWorkspaceContext = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext)
  if (context === undefined) {
    throw new Error('useWorkspaceContext must be used within a WorkspaceProvider')
  }
  return context
}

export default WorkspaceContext
